import type {
  JsonData,
  JsonDataItem,
  OdinKey,
  VisibilityButtonSetting,
  VisibilityFieldSettingType,
  VisibilityFormField,
} from '../types';
import { isArrayOf } from '../utils';
import type { VisibilityCondition } from './visibility-condition.interfaces';
import type { VisibilityConditionBase } from './visibility-condition-base';

/**
 * Minimal lookup interface for the field-settings flat map.
 * Using a custom interface instead of ReadonlyMap avoids variance issues
 * when odin-components passes Map<React.Key, DynamicFormField> (React.Key
 * includes bigint, while OdinKey is string | number).
 */
interface FieldSettingsLookup {
  has(key: OdinKey): boolean;
  get(key: OdinKey): VisibilityFormField | undefined;
}

export class FieldSetting<
  TFieldSetting extends VisibilityFieldSettingType = VisibilityFieldSettingType,
> {
  private _hasSettingChanged = false;
  private fieldSetting: TFieldSetting;
  private conditionGroups: Array<Array<VisibilityConditionBase>>;
  private fieldSettingsFlatMap?: FieldSettingsLookup;

  public get hasSettingChanged() {
    return this._hasSettingChanged;
  }

  public get isButtonSetting(): boolean {
    return this.isFormButton(this.fieldSetting);
  }

  public get field(): TFieldSetting {
    return this.fieldSetting;
  }

  constructor(
    fieldSetting: TFieldSetting,
    conditionGroups: Array<Array<VisibilityConditionBase>>,
    fieldSettingsFlatMap?: FieldSettingsLookup,
  ) {
    this.fieldSetting = fieldSetting;
    this.conditionGroups = conditionGroups;
    this.fieldSettingsFlatMap = fieldSettingsFlatMap;
    //  targetValueId array as strings instead of numbers.
    const conditions = conditionGroups.flat();
    const entityIdConditions = conditions.filter(
      (condition) =>
        condition.fieldCondition.targetValueType === 'ENTITY_ID' &&
        isArrayOf(condition.fieldCondition.targetValueId, 'string'),
    );
    if (entityIdConditions.length > 0) {
      for (let i = 0, length = entityIdConditions.length; i < length; i++) {
        const condition = entityIdConditions[i];
        const targetValueWritable = Object.getOwnPropertyDescriptor(
          condition.fieldCondition,
          'targetValueId',
        )?.writable;
        if (
          Array.isArray(condition.fieldCondition.targetValueId) &&
          targetValueWritable === true
        ) {
          const numberArray =
            condition.fieldCondition.targetValueId.map(Number);
          condition.fieldCondition.targetValueId = [...numberArray];
        }
      }
    }
  }

  /**
   * Runs the visibility condition groups to determine if a field should be visible.
   * @param {JsonDataItem} data - The updated data used to determine if the field should be visible.
   * @param {JsonData} groups - The user groups used to determine if the field should be visible.
   * @param {FieldSetting} parentFieldSetting - If the parent field setting is hidden then these conditions will not get run.
   */
  public setFieldVisibility(
    data: JsonDataItem,
    groups?: JsonData,
    parentFieldSetting?: FieldSetting,
  ) {
    let currentHiddenValue = true;
    if (!parentFieldSetting || !parentFieldSetting.fieldSetting.hidden) {
      for (let i = 0, length = this.conditionGroups.length; i < length; i++) {
        if (i === 0) {
          // the first group always runs
          currentHiddenValue = this.shouldBeHidden(
            this.conditionGroups[i],
            data,
            groups,
          );
        } else {
          // each subsequent group runs only if the previous group was successful, if not it breaks out of the loop
          if (!currentHiddenValue) {
            currentHiddenValue = this.shouldBeHidden(
              this.conditionGroups[i],
              data,
              groups,
            );
          } else {
            break;
          }
        }
      }
    }

    this._hasSettingChanged = this.fieldSetting.hidden !== currentHiddenValue;
    this.fieldSetting.hidden = currentHiddenValue;
  }

  /**
   * Runs a series of conditions to determine if the result would be to show the field or button.
   * @param {Array<VisibilityConditionBase>} conditions - The visibility conditions to evaluate.
   * @param {JsonDataItem} data - The data used to evaluate the conditions.
   * @param {JsonData} groups - The user groups used to evaluate the conditions.
   * @returns {boolean} Determines if the conditions would cause a field or button to be hidden or not.
   * @private
   */
  private shouldBeHidden(
    conditions: Array<VisibilityConditionBase>,
    data: JsonDataItem,
    groups?: JsonData,
  ): boolean {
    let isHidden = true;

    for (let i = 0, length = conditions.length; i < length; i++) {
      let _fieldFormSetting;

      if (
        this.fieldSettingsFlatMap &&
        conditions[i].sourceFieldId &&
        this.fieldSettingsFlatMap.has(conditions[i].sourceFieldId!)
      ) {
        _fieldFormSetting = this.fieldSettingsFlatMap.get(
          conditions[i].sourceFieldId!,
        );
      }

      if (_fieldFormSetting && _fieldFormSetting.hidden === true) {
        if (_fieldFormSetting.permissions?.read === false) {
          if (this.fieldSetting?.isVisibleForced !== undefined) {
            // Target has a forced visibility override from the server. Respect it
            // and short-circuit so no subsequent condition can overwrite the forced result.
            isHidden = !this.fieldSetting.isVisibleForced;
            break;
          } else {
            // Source field is hidden only because of permission defaults (e.g. new records where
            // the server hasn't yet confirmed visibility). The field may still have a value, so
            // evaluate the condition normally rather than short-circuiting to hidden.
            if (conditions[i].VisibilityType !== 'GROUP') {
              isHidden = conditions[i].runVisibilityCondition(data);
            } else {
              isHidden = groups
                ? conditions[i].runVisibilityCondition(groups)
                : true;
            }

            if (
              !this.runNextCondition(isHidden, conditions[i].fieldCondition)
            ) {
              break;
            }
          }
        } else if (
          conditions.length === 1 ||
          conditions[i].fieldCondition.conjugate === 'AND'
        ) {
          // the field we are checking is hidden, so we can skip any additional checks if there is no need to run the other conditions
          //force the field to be hidden, an earlier condition might have set it to visible
          isHidden = true;
          break;
        }
      } else {
        if (conditions[i].VisibilityType !== 'GROUP') {
          isHidden = conditions[i].runVisibilityCondition(data);
        } else {
          if (groups) {
            isHidden = conditions[i].runVisibilityCondition(groups);
          } else {
            isHidden = true;
          }
        }

        if (!this.runNextCondition(isHidden, conditions[i].fieldCondition)) {
          break;
        }
      }
    }

    return isHidden;
  }

  /**
   * Determines if the next visibility condition should be run. The purpose of visibility condition(s) is to set a field
   * to visible, with that understanding here is how isHidden in evaluated.
   *
   * 1. If the current isHidden value is true and the conjugate is AND then there is no reason to run the other
   * conditions because all conditions must be met for the field to be set visible.
   *
   * 2. If the current isHidden value is false and the conjugate is OR then there is no reason to run the other
   * conditions because only one of them must be met for the field to be set visible.
   *
   * @param {boolean} currentHiddenValue - The current hidden field value.
   * @param {VisibilityCondition} condition - The current condition to check.
   * @returns {boolean} Determines if the next visibility condition should be run.
   * @private
   */
  private runNextCondition(
    currentHiddenValue: boolean,
    condition: VisibilityCondition,
  ): boolean {
    if (currentHiddenValue && condition.conjugate === 'AND') {
      return false;
    } else if (!currentHiddenValue && condition.conjugate === 'OR') {
      return false;
    }

    return true;
  }

  private isFormButton(type: TFieldSetting) {
    return (type as VisibilityButtonSetting).name !== undefined;
  }
}
